import { env } from "@/lib/env";
import {
  DEFAULT_STORE_AVAILABILITY,
  type StoreAvailability,
} from "@/lib/landing-config";
import type { StoreLinkTouch } from "@/lib/utm";

export type StorePlatform = "ios" | "android";

type StoreUrlOptions = {
  /** utm_campaign value (default: landing_hero). */
  campaign?: string;
  /** Optional utm_content (e.g. navbar, hero). */
  content?: string;
  /** Apple provider token, pt=. */
  appleProviderToken?: string;
  /** Apple campaign token, ct=. */
  appleCampaignToken?: string;
  /** When true, set Play referrer to the URL-encoded UTM set. */
  playReferrer?: boolean;
};

export const FOUNDER_LANDING_CAMPAIGN = "founder_2026";
export const FOUNDER_LANDING_CONTENT = "landing_founder";
export const FOUNDER_APPLE_PROVIDER_TOKEN = "117695089";
export const FOUNDER_APPLE_CAMPAIGN_TOKEN = "founder_landing";

const ISO2 = /^[A-Z]{2}$/;

export function normalizeCountryCode(
  value: string | null | undefined,
): string | null {
  if (!value) return null;
  const code = value.trim().toUpperCase();
  if (!ISO2.test(code)) return null;
  if (code === "UK") return "GB";
  return code;
}

export function normalizeStoreAvailability(
  raw: unknown,
): StoreAvailability {
  const data = (raw ?? {}) as Partial<StoreAvailability>;
  return {
    android: normalizeCountryList(
      data.android,
      DEFAULT_STORE_AVAILABILITY.android,
    ),
    ios: normalizeCountryList(data.ios, DEFAULT_STORE_AVAILABILITY.ios),
  };
}

function normalizeCountryList(
  value: unknown,
  fallback: readonly string[],
): string[] {
  if (!Array.isArray(value)) return [...fallback];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const item of value) {
    const code = typeof item === "string" ? normalizeCountryCode(item) : null;
    if (!code || seen.has(code)) continue;
    seen.add(code);
    out.push(code);
  }
  return out;
}

/**
 * Visitor country for store CTA gating (client only, no main API).
 * `?country=` override → CF-IPCountry (cookie/meta if present) → navigator.language region.
 */
export function resolveVisitorCountry(input: {
  search?: string | null;
  cfIpCountry?: string | null;
  navigatorLanguage?: string | null;
}): string | null {
  if (input.search) {
    try {
      const params = new URLSearchParams(
        input.search.startsWith("?") ? input.search : `?${input.search}`,
      );
      const override = normalizeCountryCode(params.get("country"));
      if (override) return override;
    } catch {
      // ignore malformed search
    }
  }

  const cf = normalizeCountryCode(input.cfIpCountry);
  if (cf) return cf;

  return regionFromLanguageTag(input.navigatorLanguage);
}

export function regionFromLanguageTag(
  tag: string | null | undefined,
): string | null {
  if (!tag) return null;
  const parts = tag.trim().replace(/_/g, "-").split("-").filter(Boolean);
  if (parts.length < 2) return null;
  // Skip script subtags (Hans, Hant, Latn) — region is 2 letters.
  for (let i = 1; i < parts.length; i += 1) {
    const part = parts[i];
    if (part.length === 2) return normalizeCountryCode(part);
  }
  return null;
}

/** Cloudflare country if a worker/cookie/meta exposed it to the page. */
export function readCfIpCountry(): string | null {
  if (typeof document === "undefined") return null;
  const meta =
    document.querySelector('meta[name="cf-ipcountry"]')?.getAttribute("content") ||
    document
      .querySelector('meta[http-equiv="cf-ipcountry"]')
      ?.getAttribute("content");
  const fromMeta = normalizeCountryCode(meta);
  if (fromMeta) return fromMeta;

  const match = document.cookie.match(/(?:^|;\s*)(?:CF-IPCountry|cf_ipcountry)=([^;]+)/i);
  return normalizeCountryCode(match?.[1] ? decodeURIComponent(match[1]) : null);
}

export function readVisitorCountryFromWindow(): string | null {
  if (typeof window === "undefined") return null;
  return resolveVisitorCountry({
    search: window.location.search,
    cfIpCountry: readCfIpCountry(),
    navigatorLanguage: window.navigator.language,
  });
}

/**
 * True when the store CTA should open the waitlist instead of the listing.
 * Unknown country → intercept (unpublished regions waitlist).
 */
export function shouldInterceptStoreCta(
  platform: StorePlatform,
  country: string | null | undefined,
  availability: StoreAvailability = DEFAULT_STORE_AVAILABILITY,
): boolean {
  const code = normalizeCountryCode(country);
  if (!code) return true;
  const list = platform === "ios" ? availability.ios : availability.android;
  return !list.includes(code);
}

export type StoreCtaDecision = "wait" | "store" | "waitlist";

/**
 * Mobile CTAs must not open the waitlist until country + config are known.
 * An unresolved country used to intercept (waitlist) and then flip to the store.
 */
export function storeCtaDecision(
  platform: StorePlatform | null,
  country: string | null | undefined,
  availability: StoreAvailability,
  ready: boolean,
): StoreCtaDecision {
  if (!ready || !platform) return "wait";
  return shouldInterceptStoreCta(platform, country, availability)
    ? "waitlist"
    : "store";
}

export function isIosUserAgent(ua: string): boolean {
  return /iPad|iPhone|iPod/i.test(ua);
}

export function isIosStoreHref(href: string): boolean {
  return href.includes("apps.apple.com");
}

export function isAndroidStoreHref(href: string): boolean {
  return (
    href.includes("play.google.com") || href.includes("market.android.com")
  );
}

export function storePlatformFromHref(href: string): StorePlatform | null {
  if (isIosStoreHref(href)) return "ios";
  if (isAndroidStoreHref(href)) return "android";
  return null;
}

/**
 * Canonical App Store / Play Store URLs with UTM tags for marketing CTAs.
 */
export function storeUrl(
  platform: StorePlatform,
  options: StoreUrlOptions = {},
): string {
  const base =
    platform === "ios" ? env.appStoreUrlIos : env.appStoreUrlAndroid;
  const url = new URL(base);
  url.searchParams.set("utm_source", "website");
  url.searchParams.set("utm_medium", "cta");
  const campaign = options.campaign ?? "landing_hero";
  url.searchParams.set("utm_campaign", campaign);
  if (options.content) {
    url.searchParams.set("utm_content", options.content);
  }
  if (options.appleProviderToken) {
    url.searchParams.set("pt", options.appleProviderToken);
  }
  if (options.appleCampaignToken) {
    url.searchParams.set("ct", options.appleCampaignToken);
  }
  if (options.playReferrer) {
    const referrer = new URLSearchParams({
      utm_source: "website",
      utm_medium: "cta",
      utm_campaign: campaign,
    });
    if (options.content) referrer.set("utm_content", options.content);
    url.searchParams.set("referrer", referrer.toString());
  }
  return url.toString();
}

const APPLE_CT_MAX = 40;
const PLAY_PACKAGE = "com.runwayready.runwayready_app";
const UTM_FIELDS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
] as const;

function cleanField(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed || undefined;
}

function hasLandingUtm(touch: StoreLinkTouch | null | undefined): boolean {
  if (!touch) return false;
  return UTM_FIELDS.some((key) => Boolean(cleanField(touch[key])));
}

/** App Store `ct`: landing utm_campaign, otherwise the button placement. Max 40 characters. */
export function appleCampaignToken(
  touch: StoreLinkTouch | null | undefined,
  placement: string,
): string {
  const raw = cleanField(touch?.utm_campaign) || cleanField(placement) || "website";
  return raw.slice(0, APPLE_CT_MAX);
}

/**
 * Play install referrer. URLSearchParams encodes the inner query so spaces,
 * accents, "&", and "=" are percent-encoded inside the referrer value.
 */
export function playReferrerValue(
  touch: StoreLinkTouch | null | undefined,
  placement: string,
): string {
  const params = new URLSearchParams();
  if (hasLandingUtm(touch)) {
    for (const key of UTM_FIELDS) {
      const value = cleanField(touch?.[key]);
      if (value) params.set(key, value);
    }
  } else {
    params.set("utm_source", "runwayready.ai");
    params.set("utm_medium", "website");
    params.set("utm_content", cleanField(placement) || "website");
  }
  const gclid = cleanField(touch?.gclid);
  if (gclid) params.set("gclid", gclid);
  return params.toString().replace(/\+/g, "%20");
}

/**
 * One store href for hero, about, promotion, and /app.
 * Omit `touch` for the no-UTM fallback used in server HTML.
 */
export function buildStoreHref(options: {
  platform: StorePlatform;
  placement: string;
  touch?: StoreLinkTouch | null;
}): string {
  const touch = options.touch ?? null;
  if (options.platform === "android") {
    const base = new URL(env.appStoreUrlAndroid);
    const id = base.searchParams.get("id")?.trim() || PLAY_PACKAGE;
    const url = new URL(`${base.origin}${base.pathname}`);
    url.searchParams.set("id", id);
    url.searchParams.set("referrer", playReferrerValue(touch, options.placement));
    return url.toString();
  }
  const base = new URL(env.appStoreUrlIos);
  const url = new URL(`${base.origin}${base.pathname}`);
  url.searchParams.set("pt", FOUNDER_APPLE_PROVIDER_TOKEN);
  url.searchParams.set("ct", appleCampaignToken(touch, options.placement));
  return url.toString();
}

/** Store hrefs for the hero badges. Campaign tokens come from the first-touch snapshot. */
export function founderLandingStoreUrl(platform: StorePlatform): string {
  return buildStoreHref({
    platform,
    placement: platform === "ios" ? "landing_hero_ios" : "landing_hero_android",
  });
}

export function detectPreferredStore(): StorePlatform | null {
  if (typeof navigator === "undefined") return null;
  const ua = navigator.userAgent;
  if (/Android/i.test(ua)) return "android";
  if (isIosUserAgent(ua)) return "ios";
  return null;
}
